import {
    postApiExtractionsByExtractionIdMappings,
    getApiExtractionsByExtractionIdStandardized,
    postApiExtractionsByExtractionId,
    getApiExtractionsByExtractionId,
    getApiExtractionsByExtractionIdExportSummarised,
    getApiExtractionsExportSummarisedZip,
    getApiExtractionsByFileIdExportOriginal,
    getApiExtractionsExportOriginalZip,
    deleteApiExtractionsDiscard,
    postApiExtractionsByExtractionIdOutliers,
    postApiExtractionsByExtractionIdLlmDiagnosesDismiss,
    getApiExtractionsByExtractionIdLabelset,
    postApiExtractionsByExtractionIdLabelset,
    postApiExtractionsByExtractionIdTable,
    putApiExtractionsByExtractionIdExtractionType,
    getApiExtractionsByExtractionIdExtractionType,
    putApiExtractionsByExtractionIdRowType,
    putApiExtractionsByExtractionIdRowSign,
    putApiExtractionsByExtractionIdDocumentTargetRow,
    putApiExtractionsByExtractionIdCellValues,
    putApiExtractionsByExtractionIdCellValuesRevert,
    putApiExtractionsByExtractionIdResetRowChanges,
    putApiExtractionsByExtractionIdColumnState,
    postApiProjectsByProjectIdFilesByFileIdExtractRentRoll,
    type PostApiExtractionsByExtractionIdMappingsData,
    type PostApiExtractionsByExtractionIdData,
    type PostApiExtractionsByExtractionIdLabelsetData,
    type TableLocation,
    type ExtractionType,
    type PutApiExtractionsByExtractionIdRowTypeData,
    type PutApiExtractionsByExtractionIdRowSignData,
    type PutApiExtractionsByExtractionIdDocumentTargetRowData,
} from "@/generated";
import type { TExportConfig } from "./types";
import { exportConfigToQuery } from "./types";
import type {
    TApiODINMessageUploadFile,
    TApiResponseExtractionSuccess,
} from "@/apis/odin/routers/TApiResponseExtraction";
import { handleApiResponse } from "./handleApiResponse";
import { handleApiResponseAssertData } from "./handleApiResponseAssertData";
import { ApiODINMessageTypeExtraction } from "@/apis/odin/TApiODINMessageType";
import type { TRowType } from "../apiODIN";

export type TRequestParamsSaveMappings = {
    updatedRows: {
        rowId: number;
        accountCode: string | null;
    }[];
} & {
    extractionId: PostApiExtractionsByExtractionIdMappingsData["path"]["extraction_id"];
};

export type TRequestParamsUpdateExtraction =
    PostApiExtractionsByExtractionIdData["body"] & {
        extractionId: PostApiExtractionsByExtractionIdData["path"]["extraction_id"];
    };

export type TRequestParamsSaveLabelset = {
    mappingRows: {
        rowId: number;
        accountCode: string | null;
    }[];
    rowSemantics: {
        rowId: number;
        rowType: TRowType;
        flipRowSign: boolean;
    }[];
} & {
    extractionId: PostApiExtractionsByExtractionIdLabelsetData["path"]["extraction_id"];
    labelsetSourceId: number | null;
};

export type TRequestParamsUpdateRowType = {
    extractionId: PutApiExtractionsByExtractionIdRowTypeData["path"]["extraction_id"];
    rowIds: number[];
    rowType: TRowType;
};

export type TRequestParamsFlipRowSign = {
    extractionId: PutApiExtractionsByExtractionIdRowSignData["path"]["extraction_id"];
    rowIds: number[];
    flipped: boolean;
};

export type TRequestParamsSetDocumentTargetRow = {
    extractionId: PutApiExtractionsByExtractionIdDocumentTargetRowData["path"]["extraction_id"];
    rowId: number | null;
};

export const createApiExtractions = ({
    getAccessToken,
    baseWsUrl,
}: {
    getAccessToken: () => Promise<string>;
    baseWsUrl: string;
}) => ({
    saveMappings: ({ extractionId, updatedRows }: TRequestParamsSaveMappings) =>
        handleApiResponseAssertData(() =>
            postApiExtractionsByExtractionIdMappings({
                body: {
                    rows: updatedRows.map(row => ({
                        rowId: row.rowId,
                        accountCode: row.accountCode ?? undefined,
                    })),
                },
                path: { extraction_id: extractionId },
            }),
        ),
    getExtraction: async (extractionId: number) =>
        handleApiResponseAssertData(() =>
            getApiExtractionsByExtractionId({
                path: { extraction_id: extractionId },
            }),
        ),
    getStandardizedExtraction: async (extractionId: number) =>
        handleApiResponseAssertData(() =>
            getApiExtractionsByExtractionIdStandardized({
                path: { extraction_id: extractionId },
            }),
        ),
    downloadExtractionExport: async ({
        extractionId,
        exportConfig,
        nonOperatingAccountsInCoa,
    }: {
        extractionId: number;
        exportConfig: TExportConfig;
        nonOperatingAccountsInCoa: boolean;
    }) =>
        handleApiResponseAssertData(() =>
            getApiExtractionsByExtractionIdExportSummarised({
                path: { extraction_id: extractionId },
                query: exportConfigToQuery(
                    exportConfig,
                    nonOperatingAccountsInCoa,
                ),
            }),
        ),
    deleteExtractions: async ({
        extractionIds,
        projectId,
        databaseId,
    }: {
        extractionIds: number[];
        projectId: number;
        databaseId: number;
    }) =>
        handleApiResponse(() =>
            deleteApiExtractionsDiscard({
                query: {
                    database_id: databaseId,
                    ids: extractionIds,
                    project_id: projectId,
                },
            }),
        ),
    dismissOutliers: async (
        extractionId: number,
        outliers: {
            rowOrder: number;
            periodId: number;
            accountLabel: string;
            dismissed: boolean;
        }[],
    ) =>
        handleApiResponse(() =>
            postApiExtractionsByExtractionIdOutliers({
                path: { extraction_id: extractionId },
                body: {
                    outliers,
                },
            }),
        ),

    dismissLlmDiagnoses: async (
        extractionId: number,
        groups: { checkIdentities: string[]; dismissed: boolean }[],
    ) =>
        handleApiResponse(() =>
            postApiExtractionsByExtractionIdLlmDiagnosesDismiss({
                path: { extraction_id: extractionId },
                body: {
                    groups,
                },
            }),
        ),
    downloadExtractionExportZip: async ({
        extractionIds,
        exportConfig,
        nonOperatingAccountsInCoa,
    }: {
        extractionIds: number[];
        exportConfig: TExportConfig;
        nonOperatingAccountsInCoa: boolean;
    }) =>
        handleApiResponse(() =>
            getApiExtractionsExportSummarisedZip({
                query: {
                    extraction_ids: extractionIds,
                    ...exportConfigToQuery(
                        exportConfig,
                        nonOperatingAccountsInCoa,
                    ),
                },
            }),
        ),
    downloadOriginalFile: async ({
        fileId,
        databaseId,
        projectId,
    }: {
        fileId: number;
        databaseId: number;
        projectId: number;
    }) =>
        handleApiResponse(() =>
            getApiExtractionsByFileIdExportOriginal({
                path: { file_id: fileId },
                query: { database_id: databaseId, project_id: projectId },
            }),
        ),
    downloadOriginalFilesZip: async ({
        fileIds,
        databaseId,
        projectId,
    }: {
        fileIds: number[];
        databaseId: number;
        projectId: number;
    }) =>
        handleApiResponse(() =>
            getApiExtractionsExportOriginalZip({
                query: {
                    file_ids: fileIds,
                    database_id: databaseId,
                    project_id: projectId,
                },
            }),
        ),
    updateExtraction: ({
        extractionId,
        extractionStatus,
        activePeriods,
    }: TRequestParamsUpdateExtraction) =>
        handleApiResponseAssertData(() =>
            postApiExtractionsByExtractionId({
                body: {
                    extractionStatus,
                    activePeriods,
                },
                path: { extraction_id: extractionId },
            }),
        ),
    updateExtractionType: async ({
        extractionId,
        extractionType,
    }: {
        extractionId: number;
        extractionType: ExtractionType;
    }) =>
        handleApiResponse(() =>
            putApiExtractionsByExtractionIdExtractionType({
                path: { extraction_id: extractionId },
                query: { extraction_type: extractionType },
            }),
        ),
    getExtractionType: async (extractionId: number) =>
        handleApiResponseAssertData(() =>
            getApiExtractionsByExtractionIdExtractionType({
                path: { extraction_id: extractionId },
            }),
        ),
    changeExtractionTable: async ({
        extractionId,
        table,
    }: {
        extractionId: number;
        table: TableLocation;
    }) =>
        handleApiResponseAssertData(() =>
            postApiExtractionsByExtractionIdTable({
                path: { extraction_id: extractionId },
                body: { tableLocation: table },
            }),
        ),
    llmReExtract: async ({
        extractionId,
        onProgress,
    }: {
        extractionId: number;
        onProgress?: (params: { percentage: number; message: string }) => void;
    }): Promise<void> => {
        const accessToken = await getAccessToken();

        return new Promise((resolve, reject) => {
            let settled = false;

            const webSocket = new WebSocket(
                `${baseWsUrl}/ws/extraction/re-extract`,
            );

            webSocket.addEventListener("open", () => {
                webSocket.send(
                    JSON.stringify({
                        extractionId,
                        token: accessToken,
                    }),
                );
            });

            webSocket.addEventListener("error", () => {
                if (!settled) {
                    settled = true;
                    reject(
                        new Error(
                            "We were unable to extract the file with AI. Please try again.",
                        ),
                    );
                }
            });

            webSocket.addEventListener(
                "message",
                (event: MessageEvent<string>) => {
                    const message = JSON.parse(event.data) as {
                        type: ApiODINMessageTypeExtraction;
                        contents?:
                            | { percentage: number; status: string }
                            | string;
                    };

                    if (
                        message.type === ApiODINMessageTypeExtraction.PROGRESS
                    ) {
                        const contents = message.contents as {
                            percentage: number;
                            status: string;
                        };
                        onProgress?.({
                            percentage: contents.percentage,
                            message: contents.status,
                        });
                        return;
                    }

                    if (message.type === ApiODINMessageTypeExtraction.FAILURE) {
                        settled = true;
                        reject(
                            new Error(
                                (message.contents as string) ??
                                    "LLM re-extraction failed",
                            ),
                        );
                        return;
                    }

                    if (
                        message.type ===
                        ApiODINMessageTypeExtraction.EXTRACTION_SUCCESS
                    ) {
                        settled = true;
                        resolve();
                        return;
                    }
                },
            );

            webSocket.addEventListener("close", () => {
                if (!settled) {
                    settled = true;
                    reject(
                        new Error(
                            "We were unable to extract the file with AI. Please try again.",
                        ),
                    );
                }
            });
        });
    },
    createExtractionFromRentRollFileByProjectId: async ({
        projectId,
        databaseId,
        fileId,
    }: {
        projectId: number;
        databaseId: number;
        fileId: number;
    }) =>
        handleApiResponseAssertData(() =>
            postApiProjectsByProjectIdFilesByFileIdExtractRentRoll({
                path: { project_id: projectId, file_id: fileId },
                query: { database_id: databaseId },
            }),
        ),
    createExtractionFromExistingFile: async ({
        fileId,
        useAiExtraction,
        onConnectionOpened,
        onConnectionClosed,
        onError,
        onProgress,
        onExtractionSucceeded,
    }: {
        fileId: number;
        useAiExtraction?: boolean;
        onConnectionOpened?: () => void;
        onConnectionClosed?: () => void;
        onError?: ({ errorMessage }: { errorMessage: string }) => void;
        onProgress?: (params: { progress: number; message: string }) => void;
        onExtractionSucceeded?: (
            message: Omit<TApiResponseExtractionSuccess, "type">,
        ) => void;
    }): Promise<TApiResponseExtractionSuccess> => {
        const ERROR_MESSAGES = {
            UPLOAD_FAILURE: "Error: The file could not be uploaded.",
            PROCESSING_FILE_FAILURE: "There was an error processing the file.",
        };

        const accessToken = await getAccessToken();

        return new Promise<TApiResponseExtractionSuccess>((resolve, reject) => {
            const webSocket = new WebSocket(`${baseWsUrl}/ws/extraction`);
            webSocket.binaryType = "blob";
            let isSettled = false;

            const rejectAndClose = (errorMessage: string) => {
                if (isSettled) return;
                isSettled = true;
                onError?.({ errorMessage });
                reject(new Error(errorMessage));
                webSocket.close();
            };

            webSocket.addEventListener("open", () => {
                webSocket.send(
                    JSON.stringify({
                        fileId,
                        token: accessToken,
                        useAiExtraction: useAiExtraction ?? false,
                    }),
                );
                onConnectionOpened?.();
            });

            webSocket.addEventListener("close", event => {
                onConnectionClosed?.();
                if (isSettled) return;
                isSettled = true;
                reject(
                    new Error(
                        event.reason ||
                            "The connection was interrupted before the extraction finished.",
                    ),
                );
            });

            webSocket.addEventListener("error", () => {
                rejectAndClose(ERROR_MESSAGES.UPLOAD_FAILURE);
            });

            webSocket.addEventListener(
                "message",
                (event: MessageEvent<string>) => {
                    let message: TApiODINMessageUploadFile;
                    try {
                        message = JSON.parse(
                            event.data,
                        ) as TApiODINMessageUploadFile;
                    } catch {
                        rejectAndClose(ERROR_MESSAGES.PROCESSING_FILE_FAILURE);
                        return;
                    }

                    if (
                        message.type === ApiODINMessageTypeExtraction.PROGRESS
                    ) {
                        onProgress?.({
                            progress: message.contents.percentage,
                            message: message.contents.status,
                        });
                        return;
                    }

                    if (message.type === ApiODINMessageTypeExtraction.FAILURE) {
                        rejectAndClose(message.contents);
                        return;
                    }

                    if (
                        message.type ===
                        ApiODINMessageTypeExtraction.EXTRACTION_SUCCESS
                    ) {
                        if (isSettled) return;
                        isSettled = true;
                        onExtractionSucceeded?.(message);
                        resolve(message);
                        return;
                    }
                    console.error(
                        `Unknown message type: ${JSON.stringify(message)}`,
                    );
                    rejectAndClose(ERROR_MESSAGES.PROCESSING_FILE_FAILURE);
                },
            );
        });
    },
    getLabelset: async (extractionId: number) =>
        handleApiResponseAssertData(() =>
            getApiExtractionsByExtractionIdLabelset({
                path: { extraction_id: extractionId },
            }),
        ),
    saveLabelset: ({
        extractionId,
        mappingRows,
        rowSemantics,
        labelsetSourceId,
    }: TRequestParamsSaveLabelset) =>
        handleApiResponseAssertData(() =>
            postApiExtractionsByExtractionIdLabelset({
                body: {
                    mappingRows: mappingRows,
                    rowSemantics: rowSemantics,
                    labelsetSourceId: labelsetSourceId,
                },
                path: { extraction_id: extractionId },
            }),
        ),

    updateRowType: ({
        extractionId,
        rowIds,
        rowType,
    }: TRequestParamsUpdateRowType) =>
        handleApiResponse(() =>
            putApiExtractionsByExtractionIdRowType({
                path: { extraction_id: extractionId },
                body: rowIds,
                query: { row_type: rowType },
            }),
        ),
    flipRowSign: ({
        extractionId,
        rowIds,
        flipped,
    }: TRequestParamsFlipRowSign) =>
        handleApiResponse(() =>
            putApiExtractionsByExtractionIdRowSign({
                path: { extraction_id: extractionId },
                body: { row_ids: rowIds, flipped },
                query: {},
            }),
        ),
    setDocumentTargetRow: ({
        extractionId,
        rowId,
    }: TRequestParamsSetDocumentTargetRow) =>
        handleApiResponse(() =>
            putApiExtractionsByExtractionIdDocumentTargetRow({
                path: { extraction_id: extractionId },
                body: { rowId },
            }),
        ),
    editCellValues: ({
        extractionId,
        cells,
    }: {
        extractionId: number;
        cells: { rowId: number; columnId: number; amount: number | null }[];
    }) =>
        handleApiResponse(() =>
            putApiExtractionsByExtractionIdCellValues({
                path: { extraction_id: extractionId },
                body: { cells },
            }),
        ),
    revertCellValues: ({
        extractionId,
        cells,
    }: {
        extractionId: number;
        cells: { rowId: number; columnId: number }[];
    }) =>
        handleApiResponse(() =>
            putApiExtractionsByExtractionIdCellValuesRevert({
                path: { extraction_id: extractionId },
                body: { cells },
            }),
        ),
    resetRowChanges: ({
        extractionId,
        rowIds,
    }: {
        extractionId: number;
        rowIds: number[];
    }) =>
        handleApiResponse(() =>
            putApiExtractionsByExtractionIdResetRowChanges({
                path: { extraction_id: extractionId },
                body: rowIds,
            }),
        ),
    setColumnState: ({
        extractionId,
        state,
        columnId,
    }: {
        extractionId: number;
        state: boolean;
        columnId?: number;
    }) =>
        handleApiResponse(() =>
            putApiExtractionsByExtractionIdColumnState({
                path: { extraction_id: extractionId },
                query: { column_id: columnId, column_state: state },
            }),
        ),
});
